import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth, hasRole } from "../lib/auth";
import { PageHeader } from "../components/Primitives";
import { Plus, Mail, Users as UsersIcon, UserPlus, X, Layers, Check, PanelLeft, Sparkles, MapPin, Pencil, Music2 } from "lucide-react";
import { toast } from "sonner";
import NavPermissionsModal from "../components/NavPermissionsModal";
import DV360MetricsModal from "../components/DV360MetricsModal";
import TikTokMetricsModal from "../components/TikTokMetricsModal";
import RegionPicker from "../components/RegionPicker";

const blankForm = () => ({
    name: "",
    region: "",
    contact_email: "",
    contact_phone: "",
    notes: "",
    agency_id: "",
    include_portal_user: true,
    portal_email: "",
    portal_password: "",
    portal_full_name: "",
});

export default function Advertisers() {
    const { user } = useAuth();
    const [list, setList] = useState([]);
    const [agencies, setAgencies] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null); // null = create; advertiser obj = edit
    const [form, setForm] = useState(blankForm());
    const [busy, setBusy] = useState(false);
    const [metricsModal, setMetricsModal] = useState(null); // { advertiser, allowed: Set, catalog: [], inherits: bool }
    const [navModal, setNavModal] = useState(null); // { id, name } — opens NavPermissionsModal in scope="advertiser"
    const [dv360MetricsModal, setDv360MetricsModal] = useState(null); // { id, name } — DV360 metric access
    const [tiktokMetricsModal, setTiktokMetricsModal] = useState(null); // { id, name } — TikTok metric access

    const openMetricsModal = async (advertiser) => {
        try {
            const { data } = await api.get(`/detailed-reports/meta/advertiser-allowed-metrics/${advertiser.id}`);
            setMetricsModal({
                advertiser,
                allowed: new Set(data.allowed_metrics),
                catalog: data.catalog,
                inherits: data.inherits,
            });
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
    };

    const saveMetrics = async () => {
        if (!metricsModal) return;
        try {
            await api.put(`/detailed-reports/meta/advertiser-allowed-metrics/${metricsModal.advertiser.id}`, {
                allowed_metrics: Array.from(metricsModal.allowed),
            });
            toast.success(`Metric access updated for ${metricsModal.advertiser.name}`);
            setMetricsModal(null);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
    };

    const toggleMetricInModal = (key) => {
        if (!metricsModal) return;
        const next = new Set(metricsModal.allowed);
        if (next.has(key)) next.delete(key); else next.add(key);
        setMetricsModal({ ...metricsModal, allowed: next });
    };

    const load = async () => {
        const { data } = await api.get("/advertisers");
        setList(data);
    };
    useEffect(() => {
        load();
        if (user.role === "super_admin") api.get("/agencies").then((r) => setAgencies(r.data));
    }, [user.role]); // eslint-disable-line

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            if (editing) {
                // Edit existing advertiser — PATCH only the mutable fields
                const payload = {
                    name: form.name,
                    region: form.region || null,
                    contact_email: form.contact_email || null,
                    contact_phone: form.contact_phone || null,
                    notes: form.notes || null,
                };
                await api.patch(`/advertisers/${editing.id}`, payload);
                toast.success("Advertiser updated");
            } else {
                const payload = {
                    name: form.name,
                    region: form.region || null,
                    contact_email: form.contact_email || null,
                    contact_phone: form.contact_phone || null,
                    notes: form.notes || null,
                };
                if (user.role === "super_admin") payload.agency_id = form.agency_id;
                if (form.include_portal_user && form.portal_email && form.portal_password) {
                    payload.portal_user = {
                        email: form.portal_email,
                        password: form.portal_password,
                        full_name: form.portal_full_name || form.name,
                    };
                }
                await api.post("/advertisers", payload);
                toast.success(form.include_portal_user ? "Advertiser + portal user created" : "Advertiser added");
            }
            setOpen(false);
            setEditing(null);
            setForm(blankForm());
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        } finally {
            setBusy(false);
        }
    };

    const startEdit = (advertiser) => {
        setEditing(advertiser);
        setForm({
            ...blankForm(),
            name: advertiser.name || "",
            region: advertiser.region || "",
            contact_email: advertiser.contact_email || "",
            contact_phone: advertiser.contact_phone || "",
            notes: advertiser.notes || "",
            agency_id: advertiser.agency_id || "",
            include_portal_user: false,  // edit flow doesn't touch the portal user
        });
        setOpen(true);
    };

    const closeModal = () => {
        setOpen(false);
        setEditing(null);
        setForm(blankForm());
    };

    const toggleActive = async (advertiser) => {
        const turningOff = advertiser.is_active;
        if (turningOff && !window.confirm(`Deactivate ${advertiser.name}? Their portal users will be blocked from logging in until you reactivate.`)) return;
        try {
            await api.patch(`/advertisers/${advertiser.id}`, { is_active: !advertiser.is_active });
            toast.success(`${advertiser.name} is now ${advertiser.is_active ? "inactive" : "active"}`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to update status");
        }
    };

    return (
        <div data-testid="advertisers-page">
            <PageHeader
                eyebrow="Clients"
                title="Advertisers"
                subtitle="Each advertiser sees only their assigned campaigns with the commercial layer applied — and gets a dedicated portal user to log in."
                actions={
                    hasRole(user, "super_admin", "admin") && (
                        <button onClick={() => setOpen(true)} className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium" style={{ background: "var(--wl-primary-hex)", color: "#ffffff" }} data-testid="advertiser-create-btn">
                            <Plus className="h-4 w-4" /> New advertiser
                        </button>
                    )
                }
            />

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {list.map((a) => (
                    <div key={a.id} className="bg-white border border-zinc-200 p-5 hover:border-zinc-300 transition-colors" data-testid={`advertiser-card-${a.id}`}>
                        <div className="flex items-start justify-between">
                            <div>
                                <div className="font-display text-lg text-zinc-900">{a.name}</div>
                                <div className="text-xs text-zinc-500 mt-0.5">{a.contact_email || "no email"}</div>
                                {a.region && (
                                    <div className="inline-flex items-center gap-1 text-[11px] text-zinc-500 mt-1">
                                        <MapPin className="h-3 w-3" /> {a.region}
                                    </div>
                                )}
                            </div>
                            <UsersIcon className="h-5 w-5 text-zinc-400" />
                        </div>
                        {a.notes && <p className="text-xs text-zinc-500 mt-3 line-clamp-2">{a.notes}</p>}

                        {/* Status row */}
                        <div className="mt-4 flex items-center gap-3 text-xs">
                            {hasRole(user, "super_admin", "admin") ? (
                                <button
                                    type="button"
                                    onClick={() => toggleActive(a)}
                                    className={`px-2 py-0.5 rounded border whitespace-nowrap inline-flex items-center gap-1.5 transition ${a.is_active
                                            ? "bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100"
                                            : "bg-zinc-100 border-zinc-300 text-zinc-500 hover:bg-zinc-200"
                                        }`}
                                    data-testid={`advertiser-active-toggle-${a.id}`}
                                    title="Click to toggle. Inactive advertisers cannot log in."
                                >
                                    <span className={`h-1.5 w-1.5 rounded-full ${a.is_active ? "bg-emerald-500" : "bg-zinc-400"}`} />
                                    {a.is_active ? "Active" : "Inactive"}
                                </button>
                            ) : (
                                <span className="px-2 py-0.5 rounded bg-zinc-100 border border-zinc-200 text-zinc-500 whitespace-nowrap">{a.is_active ? "Active" : "Inactive"}</span>
                            )}
                            {a.contact_email && (
                                <a href={`mailto:${a.contact_email}`} className="inline-flex items-center gap-1 text-zinc-500 hover:text-zinc-900 whitespace-nowrap">
                                    <Mail className="h-3 w-3" />contact
                                </a>
                            )}
                        </div>

                        {/* Action buttons row — wraps naturally on narrow cards */}
                        {hasRole(user, "super_admin", "admin") && (
                            <div className="mt-3 pt-3 border-t border-zinc-100 flex flex-wrap items-center gap-2">
                                <button
                                    onClick={() => startEdit(a)}
                                    className="inline-flex items-center gap-1 px-2 py-1 rounded border border-zinc-200 text-xs text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900 whitespace-nowrap"
                                    data-testid={`advertiser-edit-btn-${a.id}`}
                                    title="Edit advertiser details"
                                >
                                    <Pencil className="h-3 w-3" /> Edit
                                </button>
                                <button
                                    onClick={() => openMetricsModal(a)}
                                    className="inline-flex items-center gap-1 px-2 py-1 rounded border border-zinc-200 text-xs text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900 whitespace-nowrap"
                                    data-testid={`advertiser-metrics-btn-${a.id}`}
                                    title="Choose which Meta detailed-report metrics this advertiser can see"
                                >
                                    <Layers className="h-3 w-3" /> Meta metrics
                                </button>
                                <button
                                    onClick={() => setDv360MetricsModal({ id: a.id, name: a.name })}
                                    className="inline-flex items-center gap-1 px-2 py-1 rounded border border-zinc-200 text-xs text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900 whitespace-nowrap"
                                    data-testid={`advertiser-dv360-metrics-btn-${a.id}`}
                                    title="Choose which DV360 detailed-report metrics this advertiser can see"
                                >
                                    <Sparkles className="h-3 w-3" /> DV360 metrics
                                </button>
                                <button
                                    onClick={() => setTiktokMetricsModal({ id: a.id, name: a.name })}
                                    className="inline-flex items-center gap-1 px-2 py-1 rounded border border-zinc-200 text-xs text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900 whitespace-nowrap"
                                    data-testid={`advertiser-tiktok-metrics-btn-${a.id}`}
                                    title="Choose which TikTok detailed-report metrics this advertiser can see"
                                >
                                    <Music2 className="h-3 w-3" style={{ color: "#FE2C55" }} /> TikTok metrics
                                </button>
                                <button
                                    onClick={() => setNavModal({ id: a.id, name: a.name })}
                                    className="inline-flex items-center gap-1 px-2 py-1 rounded border border-zinc-200 text-xs text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900 whitespace-nowrap"
                                    data-testid={`advertiser-nav-btn-${a.id}`}
                                    title="Choose which sidebar items this advertiser's portal users can see"
                                >
                                    <PanelLeft className="h-3 w-3" /> Sidebar access
                                </button>
                            </div>
                        )}
                    </div>
                ))}
                {!list.length && (
                    <div className="col-span-full bg-zinc-50 border border-dashed border-zinc-200 p-10 text-center text-zinc-500">No advertisers yet.</div>
                )}
            </div>

            {open && (
                <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={closeModal}>
                    <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-zinc-50 border border-zinc-200 w-full max-w-xl p-6 rounded-md my-8" data-testid={editing ? "advertiser-edit-modal" : "advertiser-create-modal"}>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-display text-xl">{editing ? `Edit ${editing.name}` : "New advertiser"}</h3>
                            <button type="button" onClick={closeModal}><X className="h-4 w-4 text-zinc-500" /></button>
                        </div>

                        <div className="space-y-3 mb-5">
                            <div className="overline">Advertiser details</div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="col-span-2">
                                    <label className="overline">Name</label>
                                    <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded" data-testid="advertiser-name-input" />
                                </div>
                                <div className="col-span-2">
                                    <label className="overline">Region <span className="text-zinc-400 lowercase normal-case">(e.g. APAC, Malaysia, India)</span></label>
                                    <RegionPicker
                                        value={form.region}
                                        onChange={(v) => setForm({ ...form, region: v })}
                                        testId="advertiser-region-picker"
                                    />
                                </div>
                                <div>
                                    <label className="overline">Contact email</label>
                                    <input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded" data-testid="advertiser-email-input" />
                                </div>
                                <div>
                                    <label className="overline">Contact phone</label>
                                    <input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded" />
                                </div>
                                {user.role === "super_admin" && !editing && (
                                    <div className="col-span-2">
                                        <label className="overline">Agency</label>
                                        <select value={form.agency_id} onChange={(e) => setForm({ ...form, agency_id: e.target.value })} required className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded" data-testid="advertiser-agency-select">
                                            <option value="">-- Select agency --</option>
                                            {agencies.map((a) => (<option key={a.id} value={a.id}>{a.name}</option>))}
                                        </select>
                                    </div>
                                )}
                                <div className="col-span-2">
                                    <label className="overline">Internal notes</label>
                                    <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded text-sm" />
                                </div>
                            </div>
                        </div>

                        {/* Portal-user section — hidden when editing an existing advertiser
                            (manage portal users from the Users page instead). */}
                        {!editing && (
                        <div className="border-t border-zinc-200 pt-4">
                            <label className="flex items-center gap-2 text-sm text-zinc-800 cursor-pointer mb-3">
                                <input type="checkbox" checked={form.include_portal_user} onChange={(e) => setForm({ ...form, include_portal_user: e.target.checked })} data-testid="advertiser-include-user" />
                                <UserPlus className="h-4 w-4 wl-accent-text" />
                                Create advertiser portal login
                            </label>
                            {form.include_portal_user && (
                                <div className="space-y-3 pl-6">
                                    <p className="text-xs text-zinc-500">Portal users only see the commercial view — never campaign spends or margin.</p>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="overline">Full name</label>
                                            <input required={form.include_portal_user} value={form.portal_full_name} onChange={(e) => setForm({ ...form, portal_full_name: e.target.value })} className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded" data-testid="advertiser-portal-name-input" />
                                        </div>
                                        <div>
                                            <label className="overline">Email</label>
                                            <input required={form.include_portal_user} type="email" value={form.portal_email} onChange={(e) => setForm({ ...form, portal_email: e.target.value })} className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded" data-testid="advertiser-portal-email-input" />
                                        </div>
                                        <div className="col-span-2">
                                            <label className="overline">Password</label>
                                            <input required={form.include_portal_user} type="password" value={form.portal_password} onChange={(e) => setForm({ ...form, portal_password: e.target.value })} className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded" data-testid="advertiser-portal-password-input" />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        )}

                        <div className="flex justify-end gap-2 mt-5">
                            <button type="button" onClick={closeModal} className="px-3 py-2 text-sm text-zinc-500 hover:text-zinc-900">Cancel</button>
                            <button type="submit" disabled={busy} className="px-4 py-2 rounded text-sm font-medium" style={{ background: "var(--wl-primary-hex)", color: "#ffffff" }} data-testid="advertiser-submit-btn">
                                {busy ? (editing ? "Saving…" : "Creating…") : (editing ? "Save changes" : "Create advertiser")}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {metricsModal && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setMetricsModal(null)}>
                    <div onClick={(e) => e.stopPropagation()} className="bg-white border border-zinc-200 rounded-md w-full max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="advertiser-metrics-modal">
                        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200">
                            <div>
                                <div className="overline">Detailed-report metric access</div>
                                <h3 className="font-display text-lg text-zinc-900">{metricsModal.advertiser.name}</h3>
                                <p className="text-xs text-zinc-500 mt-1">
                                    Pick which metrics this advertiser&apos;s portal users can see in Detailed Reports.
                                </p>
                                {metricsModal.inherits && (
                                    <p className="text-[11px] text-amber-600 mt-1">Currently inheriting the agency-wide allow-list. Save to override.</p>
                                )}
                            </div>
                            <button onClick={() => setMetricsModal(null)} className="text-zinc-400 hover:text-zinc-900"><X className="h-5 w-5" /></button>
                        </div>
                        <div className="flex items-center justify-between px-5 py-2 border-b border-zinc-100 text-xs">
                            <span className="text-zinc-500">{metricsModal.allowed.size} of {metricsModal.catalog.length} metrics enabled</span>
                            <div className="flex items-center gap-3">
                                <button onClick={() => setMetricsModal({ ...metricsModal, allowed: new Set(metricsModal.catalog) })} className="wl-accent-text hover:underline" data-testid="advertiser-metrics-select-all">Select all</button>
                                <span className="text-zinc-300">·</span>
                                <button onClick={() => setMetricsModal({ ...metricsModal, allowed: new Set() })} className="text-zinc-500 hover:text-zinc-900" data-testid="advertiser-metrics-clear">Clear</button>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2 px-5 py-4">
                            {metricsModal.catalog.length === 0 && (
                                <div className="col-span-full text-xs text-zinc-500 py-3">
                                    No metrics available to grant — the super admin has not enabled any non-internal metrics for this agency.
                                </div>
                            )}
                            {metricsModal.catalog.map((key) => {
                                const checked = metricsModal.allowed.has(key);
                                const label = key === "display_spend" ? "Spend" : key.replace(/_/g, " ");
                                return (
                                    <label key={key} className="flex items-center gap-2 px-2 py-1.5 hover:bg-zinc-50 rounded cursor-pointer text-sm" data-testid={`advertiser-metric-access-${key}`}>
                                        <span className={`h-4 w-4 rounded border flex items-center justify-center ${checked ? "wl-accent-bg wl-accent-border" : "border-zinc-300 bg-white"}`}>
                                            {checked && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                                        </span>
                                        <input type="checkbox" checked={checked} onChange={() => toggleMetricInModal(key)} className="sr-only" />
                                        <span className="text-zinc-700 capitalize">{label}</span>
                                    </label>
                                );
                            })}
                        </div>
                        <div className="flex justify-end gap-2 px-5 py-4 border-t border-zinc-200">
                            <button type="button" onClick={() => setMetricsModal(null)} className="px-3 py-2 text-sm text-zinc-500 hover:text-zinc-900">Cancel</button>
                            <button type="button" onClick={saveMetrics} className="px-4 py-2 rounded text-sm font-medium" style={{ background: "var(--wl-primary-hex)", color: "#ffffff" }} data-testid="advertiser-metrics-save">Save changes</button>
                        </div>
                    </div>
                </div>
            )}

            {navModal && (
                <NavPermissionsModal
                    scope="advertiser"
                    id={navModal.id}
                    name={navModal.name}
                    onClose={() => setNavModal(null)}
                />
            )}

            {dv360MetricsModal && (
                <DV360MetricsModal
                    scope="advertiser"
                    id={dv360MetricsModal.id}
                    name={dv360MetricsModal.name}
                    onClose={() => setDv360MetricsModal(null)}
                />
            )}

            {tiktokMetricsModal && (
                <TikTokMetricsModal
                    scope="advertiser"
                    id={tiktokMetricsModal.id}
                    name={tiktokMetricsModal.name}
                    onClose={() => setTiktokMetricsModal(null)}
                />
            )}
        </div>
    );
}
